import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Entity } from "@app/entity/model/entity";
import { EntityInfo } from "@app/entity/model/entityinfo";
import { EntityInfoService } from "@app/entity/services/entityinfo.service";
import { AlulaDealer } from "@app/project/model/alula-dealer";
import { AlulaDevice } from "@app/project/model/alula-device";
import { AlulaUser } from "@app/project/model/alula-user";
import { Project } from "@app/project/model/project";
import { SecuritySystem } from "@app/project/model/security-system";
import { AlulaReceiverService } from "@app/project/services/alula-receiver.service";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { Crc32 } from "@app/project/services/crc32";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectService } from "@app/project/services/project.service";
import { AlulaActions, ProjectActions } from "@app/project/store";
import { AlulaDispatchers } from "@app/project/store/dispatchers/alula.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ClassImporterExporter } from "@app/shared/resources/class-importer-exporter.service";
import { FormatPhonePipe } from "@app/shared/resources/phone-formatter.pipe";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { minLength } from "@app/shared/validators";
import { AppErrorStateMatcher } from "@app/shared/validators/app-error-state-matcher.service";
import { User } from "@app/user/model/user";
import { Action } from "@ngrx/store";
import { Observable, Subject, combineLatest, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, startWith, take, takeUntil, tap } from "rxjs/operators";

@Component({
    selector: "clare-monitoring-services-form",
    templateUrl: "./monitoring-services-form.component.html",
    styleUrls: ["./monitoring-services-form.component.scss"],
})
export class MonitoringServicesFormComponent implements OnInit, OnDestroy {
    @Input() dbSecurityDevice: SecuritySystem;
    @Input() alulaDealerId: string;
    @Input() project: Project;
    @Input() clareOneCallerIdNumber: string;
    @Input() isClareOne: boolean;
    @Input() isProjectOnline: boolean;
    @Input() receiverGroupSource$: Observable<any[] | Error>;
    @Input() centralStationsList$: Observable<any>;
    @Input() entity: Entity;
    @Input() alulaDealer: AlulaDealer;
    @Input() shouldShowSpinnerOnSubmit = false;
    @Input() isReplacePanelStep = false;
    @Input() saveButtonText = "Save";
    isSaving = false;
    alarmReportingNumbers$: Observable<EntityInfo[]>;
    @Input() alulaDevice$: Observable<ApiResultState<AlulaDevice>>;
    @Input() defaultDevice?: Partial<AlulaDevice>; // Default form values
    @Output() doneSubmittingForm = new EventEmitter();
    csPhoneNumber: any;
    checkValidReceiversObj$: Observable<{ hasValidReceivers: boolean; hasInvalidReceivers: boolean }>;

    alulaCustomer: AlulaUser;

    @Output() onFormCompleted = new EventEmitter<Action>();

    crcValidators = Validators.compose([Validators.required, Validators.minLength(4), Validators.maxLength(4)]);
    serialValidators = Validators.required;

    unsubscribe$ = new Subject<void>();
    isSubmittingForm$: Observable<boolean>;
    private centralStation;
    private oldCentralStation;
    monitoringServicesForm: FormGroup = this.formBuilder.group({
        id: [],
        csPhoneNumber: [""],
        receiverGroupId: [""],
        callerIdPhoneNumber: ["", Validators.compose([Validators.pattern("^[0-9]+$"), Validators.minLength(10)])],
        csAccountNumber: ["", Validators.compose([minLength(4), Validators.required])],
        csAuxNumber: [],
        macAddress: [
            "",
            Validators.compose([
                Validators.required,
                Validators.pattern("^[a-fA-F0-9]{2}(?:[:-]?[a-fA-F0-9]{2}){5}(?:,[a-fA-F0-9]{2}(?:[:-]?[a-fA-F0-9]{2}){5})*$"),
            ]),
        ],
        crcCode: ["", this.crcValidators],
        serialNumber: ["", this.serialValidators],
        openCloseSuppression: [true],
        customerId: [""],
    });

    get fields() {
        return this.monitoringServicesForm.controls;
    }

    constructor(
        private formBuilder: FormBuilder,
        public errorMatcher: AppErrorStateMatcher,
        private projectService: ProjectService,
        private chServiceService: CHServiceService,
        private dispatcher: ProjectDispatchers,
        private crc32: Crc32,
        private alulaDispatcher: AlulaDispatchers,
        private readonly alulaReceiverService: AlulaReceiverService,
        private readonly entityInfoService: EntityInfoService,
        private readonly deleteConfirmationService: DeleteConfirmationService
    ) {}

    ngOnInit() {
        this.checkValidReceiversObj$ = combineLatest([
            this.monitoringServicesForm.controls.receiverGroupId.valueChanges.pipe(
                startWith(this.monitoringServicesForm.controls.receiverGroupId.value),
                map(_ => this.monitoringServicesForm.controls.receiverGroupId.value)
            ),
            this.alulaReceiverService.getCentralStationsList(this.alulaDealerId, true),
        ]).pipe(
            map(([receiverGroupId, csList]) => csList.find(centralStation => receiverGroupId === centralStation.id)),
            map(currentCentralStation => this.alulaReceiverService.doesHaveValidReceivers(currentCentralStation))
        );
        this.monitoringServicesForm.controls.macAddress.setValue(this.dbSecurityDevice?.macAddress?.toUpperCase());

        if (this.project.claresystem && this.project.claresystem.serialNumber) {
            this.monitoringServicesForm.controls.serialNumber.setValue(this.project.claresystem.serialNumber);
        }

        combineLatest([
            this.alulaDispatcher.selectAlulaUser$().pipe(map(alulaUser => (alulaUser ? alulaUser.result : null))),
            this.projectService.getActiveProjectUser$(),
        ])
            .pipe(
                takeUntil(this.unsubscribe$),
                tap(([alulaUser, clareUser]: [AlulaUser, User]) => {
                    let user;
                    if (this.alulaDealerId && clareUser) {
                        // Merge Clare Customer info with Alula User info
                        user = alulaUser || AlulaUser.fromClareUser(clareUser, this.alulaDealerId);

                        if (user) {
                            user.firstName = clareUser.firstName;
                            user.lastName = clareUser.lastName;
                            user.email = clareUser.email;
                        }
                    }

                    this.alulaCustomer = user;
                })
            )
            .subscribe();

        this.isSubmittingForm$ = this.alulaDispatcher.selectIsLoadingSecurityMonitoring$();

        this.monitoringServicesForm.controls.macAddress.valueChanges
            .pipe(debounceTime(500), takeUntil(this.unsubscribe$), distinctUntilChanged())
            .subscribe(value => {
                const matches = (value || "").replace(/:/g, "").match(/([0-9a-fA-F]){1,2}/g) || [];

                this.monitoringServicesForm.controls.macAddress.setValue(matches.join(":").toUpperCase());
            });

        this.monitoringServicesForm.controls.callerIdPhoneNumber.valueChanges
            .pipe(debounceTime(500), takeUntil(this.unsubscribe$), distinctUntilChanged())
            .subscribe(value => {
                this.monitoringServicesForm.controls.callerIdPhoneNumber.setValue((value || "").replace(/\+/g, ""));
            });

        this.alulaDevice$ = this.alulaDevice$?.pipe(
            tap(alulaDeviceCall => {
                this.updateAlulaDevice(alulaDeviceCall);
            })
        );
        this.checkForAlarmReportingNumbers();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    checkForAlarmReportingNumbers() {
        this.alarmReportingNumbers$ = this.entityInfoService.selectCentralStations(this.project.entityId).pipe(
            take(1),
            shareReplay(1),
            tap(arns => {
                if (arns?.length) {
                    // If there are ARNs still here, then we should not have errors selecting a receiver group
                    this.monitoringServicesForm.controls.receiverGroupId.setValidators(null);
                    this.monitoringServicesForm.controls.receiverGroupId.setErrors(null);
                }
            })
        );
    }

    private updateAlulaDevice(alulaDeviceCall) {
        if (this.clareOneCallerIdNumber) {
            this.monitoringServicesForm.controls.callerIdPhoneNumber.setValue(this.clareOneCallerIdNumber);
        }

        if (alulaDeviceCall?.callState === LoadingState.loaded) {
            const alulaDevice = alulaDeviceCall.result;

            if (alulaDevice) {
                this.monitoringServicesForm.patchValue(alulaDevice);
                const formattedPhone = new FormatPhonePipe().transform(
                    alulaDevice.csPhoneNumber || this.defaultDevice?.csPhoneNumber || "",
                    "x (xxx) xxx-xxxx"
                );

                this.monitoringServicesForm.controls.csPhoneNumber.setValue(formattedPhone || "");
                // TODO: These trigger change detection errors
                this.monitoringServicesForm.controls.serialNumber.setValidators(null);
                this.monitoringServicesForm.controls.crcCode.setValidators(null);
            } else if (this.isClareOne) {
                this.monitoringServicesForm.controls.serialNumber.setValidators(this.serialValidators);
                this.monitoringServicesForm.controls.crcCode.setValidators(null);
            } else {
                this.monitoringServicesForm.controls.crcCode.setValidators(this.crcValidators);
                this.monitoringServicesForm.controls.serialNumber.setValidators(null);
            }
        }

        this.monitoringServicesForm.controls.callerIdPhoneNumber.setValue(
            new FormatPhonePipe().transform(this.monitoringServicesForm.controls.callerIdPhoneNumber.value, "(xxx) xxx-xxxx")
        );

        // Update still-empty fields with defaults
        const controls = this.monitoringServicesForm.controls;
        this.monitoringServicesForm.patchValue({
            receiverGroupId: controls.receiverGroupId.value || this.defaultDevice?.receiverGroupId || "",
            callerIdPhoneNumber: controls.callerIdPhoneNumber.value || this.defaultDevice?.callerIdPhoneNumber || "",
            csAccountNumber: controls.csAccountNumber.value || this.defaultDevice?.csAccountNumber || "",
            csAuxNumber: controls.csAuxNumber.value || this.defaultDevice?.csAuxNumber || "",
            macAddress: controls.macAddress.value || this.defaultDevice?.macAddress || "",
            crcCode: controls.crcCode.value || this.defaultDevice?.crcCode || "",
        });
    }

    upgradeToIP(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        this.alulaDispatcher.updateAlulaDevice(Object.assign({}, alulaDeviceCall.result, { relayGroupEnabled: true }));
    }

    private prepareAlulaDevice(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        const securityPanelData: any = ClassImporterExporter.exportObject(this.monitoringServicesForm.getRawValue(), AlulaDevice.importMap);

        if (!alulaDeviceCall.result?.dealerId || alulaDeviceCall.result.dealerId === this.alulaDealerId) {
            securityPanelData.csPhoneNumber = AlulaDevice.unformatPhone(securityPanelData.csPhoneNumber);
            securityPanelData.callerIdPhoneNumber = AlulaDevice.unformatPhone(securityPanelData.callerIdPhoneNumber);

            securityPanelData.openCloseSuppression = securityPanelData.openCloseSuppression ? 1 : 0;
            securityPanelData.dealerId = this.alulaDealerId;
            securityPanelData.receiverGroupId = this.monitoringServicesForm.controls.receiverGroupId?.value;
            // Set relayGroupEnabled to true if a receiverGroupId is set
            securityPanelData.relayGroupEnabled = !!securityPanelData.receiverGroupId || alulaDeviceCall?.result?.relayGroupEnabled;

            return AlulaDevice.fromDbObject({
                id: securityPanelData.id,
                attributes: securityPanelData,
                type: "device",
            });
        }

        return null;
    }

    private setCRCFromSerialAndUpdateProjectController(serialNumber: string) {
        if (serialNumber) {
            let crc = this.crc32.getCrc("0xEDB88320", serialNumber);
            crc = crc.substr(0, 4);
            this.project.claresystem.serialNumber = serialNumber;
            this.monitoringServicesForm.controls.crcCode.setValue(crc);
        }
    }

    private updateProjectController() {
        this.doneSubmittingForm.emit();
        this.dispatcher.dispatch(new ProjectActions.UpdateProjectController(this.project.claresystem, this.project, this.dbSecurityDevice));
    }
    private getCSById(Id) {
        this.alulaReceiverService
            .getAlulaReceiverGroupById(Id)
            .pipe(
                take(1),
                tap(group => {
                    this.centralStation = group;
                })
            )
            .subscribe();
    }
    saveForm(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        if (this.monitoringServicesForm.controls.callerIdPhoneNumber.value === "") {
            this.monitoringServicesForm.controls.callerIdPhoneNumber.markAsTouched();
        }
        if (this.monitoringServicesForm.valid) {
            this.isSaving = true;
            this.dispatcher.dispatch(new AlulaActions.LoadAlulaStateItem("monitoringServices"));
            this.onFormCompleted.subscribe(action => {
                this.isSaving = false;
                // calls updateProjectController to update the controller with the serial number
                // on Clare One panel only when enable security monitoring enabled action is successful
                if (
                    action.type.indexOf("Success") &&
                    (!this.project.claresystem.serialNumber || !this.project.securitysystem || !this.project.securitysystem.id)
                ) {
                    this.updateProjectController();
                }
            });
            if (this.isClareOne) {
                const serialNumber = this.monitoringServicesForm.value.serialNumber;
                let serialVerificationResult = of({ error: null });

                // If there is no existing device from Alula, verify the serial number
                if (!alulaDeviceCall?.result) {
                    serialVerificationResult = this.chServiceService
                        .verifySerialNumber(ProjectHelperService.getProjectUuid(this.project), serialNumber)
                        .pipe(catchError(e => of({ error: "Could not verify serial number" })));
                }

                serialVerificationResult.subscribe(result => {
                    if (result && !result.error) {
                        this.setCRCFromSerialAndUpdateProjectController(serialNumber);
                        this.dispatchEnableSecurityMonitoringAction(alulaDeviceCall);
                    } else {
                        let msg = "The serial number was invalid.";

                        if (result.error) {
                            msg = result.error;
                        } else {
                            this.monitoringServicesForm.markAsTouched();
                            this.monitoringServicesForm.markAsDirty();
                            this.fields.serialNumber.setErrors({
                                invalid: "Invalid Serial Number",
                            });
                        }

                        this.dispatcher.dispatch(new AlulaActions.EnableSecurityMonitoringFailure(new DataServiceError(msg, null)));
                    }
                });
            } else {
                this.dispatchEnableSecurityMonitoringAction(alulaDeviceCall);
            }
        }
    }

    dispatchEnableSecurityMonitoringAction(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        const device = this.prepareAlulaDevice(alulaDeviceCall);
        if (!device) {
            const msg = "The security device is assigned to another dealer. Please contact support to correct this issue.";
            this.dispatcher.dispatch(new AlulaActions.EnableSecurityMonitoringFailure(new DataServiceError(msg, null)));
            return;
        }
        this.getCSById(this.monitoringServicesForm.controls.receiverGroupId.value);
        let dataUpdate: any = {};
        dataUpdate = {
            cs: this.centralStation,
            arn: device.csPhoneNumber,
            isModify: false,
            isActivate: !alulaDeviceCall.result?.customerId,
        };
        if (
            alulaDeviceCall.result?.receiverGroupId !== this.monitoringServicesForm.controls.receiverGroupId.value ||
            alulaDeviceCall.result?.csAccountNumber !== this.monitoringServicesForm.controls.csAccountNumber.value ||
            alulaDeviceCall.result?.csPhoneNumber !== this.monitoringServicesForm.controls.csPhoneNumber.value
        ) {
            this.alulaReceiverService
                .getAlulaReceiverGroupById(alulaDeviceCall.result?.receiverGroupId)
                .pipe(
                    take(1),
                    tap(group => {
                        this.oldCentralStation = group;
                    })
                )
                .subscribe();
            if (!!this.oldCentralStation) {
                dataUpdate = {
                    ...dataUpdate,
                    isModify: true,
                    originalCs: this.oldCentralStation,
                    originalCsAccountNumber: alulaDeviceCall.result?.csAccountNumber,
                    originalArn: alulaDeviceCall.result?.csPhoneNumber,
                };
            }
        }

        const payload = {
            dealerId: this.alulaDealerId,
            device,
            user: this.alulaCustomer,
            project: this.project,
            emitter: this.onFormCompleted,
            dataUpdate,
            entityId: this.project.entityId,
            projectId: this.project.id,
        };

        return this.dispatcher.dispatch(new AlulaActions.EnableSecurityMonitoring(payload));
    }
    disableSecurityMonitoring(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        if (alulaDeviceCall?.result?.receiverGroupId) {
            this.getCSById(alulaDeviceCall.result.receiverGroupId);
        }

        this.deleteConfirmationService
            .open(
                { warning: "Security Monitoring? This will stop security events from being sent to the Central Station" },
                AlulaActions.DeactivateSecurityMonitoringConfirmationDismiss,
                AlulaActions.DeactivateSecurityMonitoring,
                null,
                "Deactivate"
            )
            .pipe(
                take(1),
                tap(response => {
                    if (response && response.type) {
                        switch (response.type) {
                            case AlulaActions.ActionTypes.DeactivateSecurityMonitoring.toString():
                                if (alulaDeviceCall?.result?.isMonitoringServiceEnabled()) {
                                    const payload = {
                                        device: this.prepareAlulaDevice(alulaDeviceCall),
                                        uuid: this.project.claresystem?.uuid,
                                        emitter: this.onFormCompleted,
                                        csName: this.centralStation?.name,
                                        entityId: this.project.entityId,
                                        projectId: this.project.id,
                                    };
                                    return this.dispatcher.dispatch(new AlulaActions.DisableSecurityMonitoring(payload));
                                }
                            /* eslint-disable no-fallthrough */
                            case AlulaActions.ActionTypes.DeactivateSecurityMonitoringConfirmationDismiss.toString():
                                return of(null);
                        }
                    }
                })
            )
            .subscribe();
    }
}
